<template>
  <div class="selected-product-list">
    <main v-if="products.length > 0" class="selected-product-list__main">
      <BaseText
        class="selected-product-list__main--title"
        size="sm"
        as="p"
        :color="NebraskaColors.textSecondary"
      >
        Produtos selecionados
      </BaseText>
      <ProductCard
        v-for="(item, index) in products"
        :key="index"
        :add-product="false"
        :product="item"
        :is-selected="
          Boolean(
            productSelected.includes(item.productId) ||
              productSelected.includes(item.id ?? '')
          )
        "
        @handle-instrument-click="handleInstrumentSelected"
      />
    </main>
    <ButtonContainer
      v-if="!isDeleteActive"
      class="selected-product-list__button-container"
    >
      <template #button-primary>
        <ButtonPrimary
          v-if="route.params.portfolioId"
          size="large"
          label="Salvar alterações"
          :loading="isLoading"
          @click="handleEditEvent"
        />
        <ButtonPrimary
          v-else
          size="large"
          label="Criar Carteira"
          :loading="isLoading"
          @click="handleAddEvent"
        />
      </template>
      <template #button-secondary>
        <ButtonSecondary
          size="large"
          label="Adicionar mais produtos"
          @click="$emit('show-wallet-selection')"
        />
      </template>
    </ButtonContainer>
    <ButtonContainer v-else class="selected-product-list__button-container">
      <template #button-primary>
        <ButtonPrimary
          size="large"
          label="Remover"
          :disabled="!productSelected.length"
          :loading="isLoadingRemove"
          @click="handleDeleteProduct"
        />
      </template>
    </ButtonContainer>

    <ModalCreateWallet
      data-testid="modal-create-wallet"
      :is-open-modal="showWalletModal"
      @close="handleAddEvent"
    />
  </div>
</template>
<script setup lang="ts">
import { ref, inject, watch } from 'vue';
import { useRoute , useRouter } from 'vue-router/composables';
import {
  BaseText,
  ButtonContainer,
  ButtonSecondary,
  ButtonPrimary
} from '@warrenbrasil/nebraska-web';
import { ModalCreateWallet } from './components/ModalCreateWallet';
import { ProductCard } from './components/ProductCard';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  IDataConfirmEdit,
  IEditPortfolioResponse,
  IProductRemove,
  IProductsRemoveParam
} from '@/modules/wallet-builder/src/services/types/models/IBffInvestments';
import { IProductGetLastState } from '@/modules/catalog/services/products-acquisition';

interface Props {
  products: IProductGetLastState[];
  isDeleteActive: boolean;
  portfolioId?: string;
}

const props = defineProps<Props>();
const emit = defineEmits(['show-wallet-selection', 'disable-delete-mode']);
const route = useRoute();

const removeProductFromWallet = inject(
  'removeProductFromWallet',
  async (params: IProductsRemoveParam) => ({ params })
);

const confirmEditPortfolio = inject(
  'confirmEditPortfolio',
  async (_: IDataConfirmEdit) => ({ name: '' } as IEditPortfolioResponse)
);

watch(
  () => props.products,
  (newValue, _) => {
    if (newValue.length === 0) {
      emit('show-wallet-selection');
    }
  }
);

watch(
  () => props.isDeleteActive,
  (newValue, _) => {
    if (!newValue) {
      productSelected.value = [];
    }
  }
);
const router = useRouter();

const productSelected = ref<string[]>([]);

const showWalletModal = ref(false);

const isLoading = ref(false);

const isLoadingRemove = ref(false);

function handleAddEvent() {
  showWalletModal.value = !showWalletModal.value;
}

async function handleEditEvent() {
  isLoading.value = true;

  const result = await confirmEditPortfolio({
    portfolioId: route.params.portfolioId!
  });

  isLoading.value = false;

  router.replace({
    name: 'wealthWalletEdited',
    params: { id: route.params.portfolioId ?? '' },
    query: { portfolioname: result?.name || '-' }
  });
}

function handleInstrumentSelected({ id }: IProductRemove) {
  if (props.isDeleteActive) {
    if (productSelected.value.includes(id)) {
      productSelected.value = productSelected.value.filter(item => item !== id);
    } else {
      productSelected.value.push(id);
    }
  }
}

async function handleDeleteProduct() {
  try {
    isLoadingRemove.value = true;
    await removeProductFromWallet({ productIds: productSelected.value });
  } finally {
    emit('disable-delete-mode');
    isLoadingRemove.value = false;
  }
}
</script>
<style lang="less" scoped>
.selected-product-list {
  display: flex;
  justify-content: center;
  padding: @size-spacing-x800;

  &__main {
    width: 100%;
    max-width: 1280px;
    margin-top: @size-spacing-x600;
    margin-bottom: @size-spacing-x2000;

    &--title {
      font-weight: 700;
      text-transform: uppercase;
      margin-bottom: @size-spacing-x350;
    }
  }

  &__button-container {
    width: 100%;
    position: fixed;
    bottom: 0;
    background-color: @background-secondary;
  }
}
</style>
